/**
 * JSON Database Renderer
 * Renders a category-based JSON structure with items grouped by blueprint/category
 */

import { ExportTemplate } from '../../../../types/exportTemplates';
import { applyNamingConvention } from '../../core/naming';
import { extractTextValue, stripHtmlTags } from '../../core/formatting';
import { extractFields } from '../../fieldMapping/extraction';
import { groupItemsByCategory } from '../../fieldMapping/processing';
import { LookupContext } from '../../fieldMapping/references';

/**
 * Format a field value for JSON output
 * Unlike code output, JSON uses native types directly
 */
function formatJsonValue(value: any, mappedType: string, isTranslatable: boolean = false): any {
  const extracted = extractTextValue(value);

  if (extracted === null || extracted === undefined || extracted === '') {
    if (mappedType.includes('number') || mappedType.includes('int')) return 0;
    if (mappedType.includes('boolean')) return false;
    if (mappedType.includes('array') || mappedType.includes('[]') || mappedType.includes('{')) return [];
    return '';
  }

  if (Array.isArray(extracted)) {
    return extracted.map(v => formatJsonValue(v, 'string'));
  }

  if (typeof extracted === 'number') return extracted;
  if (typeof extracted === 'boolean') return extracted;
  if (typeof extracted === 'string') return extracted;

  return String(extracted);
}

/**
 * Render database template (JSON category-based pattern)
 *
 * @param template - The export template configuration
 * @param items - The items to export
 * @param blueprints - Available blueprints
 * @param types - Optional types data for resolving type references
 * @param tags - Optional tags data for resolving tag references
 * @param allItemsForLookup - Optional all items for resolving glossaryItem references
 */
export function renderJsonDatabaseTemplate(
  template: ExportTemplate,
  items: any[],
  blueprints: any[],
  types?: any[],
  tags?: any[],
  allItemsForLookup?: any[]
): string {
  const categories = groupItemsByCategory(items, blueprints, template.categoryMapping);

  const lookupContext: LookupContext = {
    types,
    tags,
    allItems: allItemsForLookup || items
  };

  const database: Record<string, Record<string, Record<string, any>>> = {};

  categories.forEach(category => {
    const categoryObj: Record<string, Record<string, any>> = {};

    category.items.forEach(item => {
      const fields = extractFields(item, template, blueprints, category.name, lookupContext);
      const itemKey = applyNamingConvention(item.name || item.id, template.namingConventions.instanceName);
      const itemObj: Record<string, any> = {};

      fields.forEach(field => {
        let fieldName = applyNamingConvention(field.label, template.namingConventions.fieldName);

        if (template.fieldNameOverrides && item.blueprint_id) {
          const blueprintOverrides = template.fieldNameOverrides[item.blueprint_id];
          if (blueprintOverrides && blueprintOverrides[field.label]) {
            const override = blueprintOverrides[field.label];
            if (override.useExportName && override.exportName) {
              fieldName = override.exportName;
            }
          }
        }

        itemObj[fieldName] = formatJsonValue(
          field.value,
          field.mappedType,
          field.isTranslatable
        );
      });

      categoryObj[itemKey] = itemObj;
    });

    database[category.name] = categoryObj;
  });

  console.log('✅ JSON database template rendered successfully');

  return JSON.stringify(database, null, 2);
}
